import { DataTable, type LocalDataColumn } from "@nymbl/nos2-rules";
import type { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { GUIDANCE_HELP } from "@/lib/column-help";
import { withCells } from "@/lib/table-columns";
import { formatHours } from "@/lib/time/rules";
import type { DemandMonthRow } from "@/schemas/month";

interface DemandTableProps {
  query: UseQueryResult<DemandMonthRow[]>;
  /** "May 2026", for the table's accessible name. */
  monthLabel: string;
}

export const GUIDANCE_COLUMNS: LocalDataColumn<DemandMonthRow>[] = [
  {
    key: "demand",
    label: "Demand",
    type: "text",
    accessor: (r) => r.demandName,
    searchText: (r) => r.accountName ?? "",
    help: GUIDANCE_HELP.demand,
  },
  {
    key: "role",
    label: "Role",
    type: "text",
    accessor: (r) => r.role,
    help: GUIDANCE_HELP.role,
  },
  {
    key: "planned",
    label: "Guidance",
    type: "number",
    accessor: (r) => r.planned,
    help: GUIDANCE_HELP.planned,
  },
  {
    key: "logged",
    label: "Logged",
    type: "number",
    accessor: (r) => r.logged,
    help: GUIDANCE_HELP.logged,
  },
  {
    key: "billable",
    label: "Billable",
    type: "number",
    accessor: (r) => r.billable,
    help: GUIDANCE_HELP.billable,
  },
];

const COLUMNS = withCells(GUIDANCE_COLUMNS, {
  demand: {
    render: (r) => (
      <>
        <div className="font-semibold">{r.demandName}</div>
        {r.accountName && <div className="text-xs text-muted-foreground">{r.accountName}</div>}
      </>
    ),
  },
  planned: { render: (r) => (r.planned === null ? "" : formatHours(r.planned)) },
  logged: {
    render: (r) =>
      r.planned !== null && r.logged > r.planned ? (
        <span className="font-semibold text-warning">{formatHours(r.logged)}</span>
      ) : (
        formatHours(r.logged)
      ),
  },
  billable: { render: (r) => formatHours(r.billable) },
});

const byDemand = (a: DemandMonthRow, b: DemandMonthRow) =>
  a.demandName.localeCompare(b.demandName) || a.demandPersonId.localeCompare(b.demandPersonId);

export function DemandTable({ query, monthLabel }: DemandTableProps) {
  const rows = useMemo(() => [...(query.data ?? [])].sort(byDemand), [query.data]);
  return (
    <DataTable
      label={`Guidance vs logged, ${monthLabel}`}
      title="Guidance vs logged"
      search={{ placeholder: "Search demands…" }}
      exportName="guidance"
      columns={COLUMNS}
      rows={rows}
      rowKey={(r) => r.demandPersonId}
      defaultSort="planned"
      defaultSortDir="desc"
      loading={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      empty={{ title: "No guidance or hours" }}
    />
  );
}
